/**
 * @file index.tsx
 *
 * @fileoverview Renders a multiple date range widget for entity fields.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React, { useContext, useEffect } from 'react';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { deriveDateFormat } from '~/jobiqo/utils/dates';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import { extractInitialDate } from '~/jobiqo/components/Form/Widgets/Date/utils';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { RangeDatePopup } from '~/vcl/atoms/01-forms/06-date/range';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../../hooks/useFieldRules';
import { DateRangeInput } from '~/jobiqo/types/types';
import { FormItem } from '~/vcl/atoms/01-forms/11-form-item';
import { FormLabel } from '~/vcl/atoms/01-forms/11-form-item/01-form-label';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { CloseIcon } from '~/vcl/icons/Close';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

const DateRangeMultipleWidget = ({ entityField, fieldRules = {} }: Props) => {
  const intl = useIntl();
  const { control, getValues, setValue } = useFormContext();
  const values = getValues();
  const { hidden, required: ruleRequired } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );

  const { locale }: AppContextInterface = useContext(AppContext);
  const dateFormat = deriveDateFormat(intl, entityField.removeDay);

  const addNew = () => {
    append({ startDate: null, endDate: null } as DateRangeInput);
  };

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    addNew();
  }

  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField.id]);

  const { fields, remove, append } = useFieldArray({
    control,
    name: entityField.id,
  });

  if (hidden) {
    return null;
  }

  return (
    <FormItem>
      <FormLabel required={entityField.required}>{entityField.label}</FormLabel>
      <FlexItems gap={3}>
        {fields.map((field, index) => {
          const fieldName = `${entityField.id}.${index}`;
          return (
            <FlexItems key={field.id}>
              <Controller
                name={fieldName}
                control={control}
                render={({ field: { value, name }, fieldState: { error } }) => (
                  <>
                    <FlexItem>
                      <RangeDatePopup
                        //label={entityField.label}
                        //description={entityField.description}
                        initialStartDate={extractInitialDate(value?.startDate)}
                        initialEndDate={extractInitialDate(value?.endDate)}
                        onDatesChange={(dates) => {
                          // Format to what drupal wants.
                          setValue(
                            name,
                            {
                              ...value,
                              startDate: dates.startDate
                                ? format(dates.startDate, 'yyyy-MM-dd')
                                : null,
                              ...(dates.endDate && {
                                endDate: dates.endDate
                                  ? format(dates.endDate, 'yyyy-MM-dd')
                                  : null,
                              }),
                            },
                            { shouldValidate: true },
                          );
                        }}
                        showMonthYearPicker={entityField.removeDay}
                        startDatePlaceholderText={
                          dateFormat ? dateFormat.toLowerCase() : ''
                        }
                        endDatePlaceholderText={
                          dateFormat ? dateFormat.toLowerCase() : ''
                        }
                        displayFormat={dateFormat}
                        required={entityField.required || ruleRequired}
                        error={!!error}
                        locale={locale}
                      />
                    </FlexItem>
                    <FlexItem>
                      <Button
                        raised={false}
                        iconed={false}
                        buttonStyle="danger"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <CloseIcon title="remove" />
                        <FormattedMessage
                          id="daterange_multiple_widget_remove_period"
                          defaultMessage="Remove"
                        />
                      </Button>
                    </FlexItem>
                    {!!error && <InlineError message={error.message} />}
                  </>
                )}
              />
              <Spacing />
            </FlexItems>
          );
        })}
      </FlexItems>
      <Spacing />
      <Button buttonStyle="secondary" onClick={handleClick}>
        <FormattedMessage
          id="daterange_multiple_widget_add"
          defaultMessage="Add period"
        />
      </Button>
    </FormItem>
  );
};

export default DateRangeMultipleWidget;
